import type { ColumnsType } from "antd/es/table";
import type { DefaultOptionType } from "antd/es/select";
import { EllipsisOutlined } from "@ant-design/icons";
import { RiArrowDownSLine } from "react-icons/ri";
import { MdDelete } from "react-icons/md";
import { Popover, Select } from "antd";
import {
  InvoiceDataType,
  UpdateInvoiceStatusType,
} from "../../../Types/invoiceManagementTypes";
import {
  InvoiceStatus,
  QuickbookStatus,
  InvoiceRecordStatus,
  UpdateInvoiceStatusKey,
} from "../../../enums/InvoiceManagementEnums";
import { PlacementPositions } from "../../../enums/PlacementPositionsEnums";
import styles from "./ListViewTable.module.scss";
import pdfIcon from "../../../assets/images/view_pdf.svg";
import clsx from "clsx";
import { getPrevDollorFormattedString } from "../../../Utilities";
import { getEnumOptions } from "../../../utils/getDropdownOptions";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { formatNumber } from "../../../utils/numberFormatterUtil";
import ArchiveIcon from "../../../assets/images/delete-contact-icon.svg";
import RecallIcon from "../../../assets/images/recall-document.svg";
import DownloadIcon from "../../../assets/images/download-arrow-icon.svg";
import { TableSortTypes } from "../../../enums/ViewListingTypes";
import { getTableSortOrder } from "../../../utils/getTableSortOrder";

const invoiceStatusOptions: DefaultOptionType[] = getEnumOptions(InvoiceStatus);
const quickbookStatusOptions: DefaultOptionType[] =
  getEnumOptions(QuickbookStatus);

export const InvoiceSortColumns = {
  INVOICE_ID: "invoiceId",
  CUSTOMER: "companyName",
  JOB: "jobName",
  ORDER: "order",
  TRUCK_NUMBER: "truckNumber",
  DATE: "date",
  AMOUNT: "amount",
};

export const getInvoiceManagemnetTableColumns = (
  handleStatusChange?: (params: UpdateInvoiceStatusType) => void,
  openArchiveModal?: (invoiceId?: string) => void,
  handleFetchingPresignedUrl?: (invoiceId?: string) => void,
  editAccess: boolean,
  handleRecallAction?: (invoiceId?: string) => void,
  handleDownloadFile?: (invoiceId?: string) => void,
  sortFilter,
): ColumnsType<InvoiceDataType> => {
  const { sortBy, sortOrder } = sortFilter || {};

  return [
    {
      title: "Invoice ID",
      key: InvoiceSortColumns.INVOICE_ID,
      align: "center",
      dataIndex: "invoiceId",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        InvoiceSortColumns.INVOICE_ID,
      ),
      render: (val, record) => (
        <span
          className={clsx("table-font-mobile", {
            [styles.invoiceIdWithDailySummary]: record?.dailySummary === true,
          })}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Customer",
      key: InvoiceSortColumns.CUSTOMER,
      align: "center",
      dataIndex: "companyName",
      width: "192px",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        InvoiceSortColumns.CUSTOMER,
      ),
      render: (val) => <span className="table-font-mobile">{val}</span>,
    },
    {
      title: "Job",
      key: InvoiceSortColumns.JOB,
      align: "center",
      width: "192px",
      dataIndex: "jobName",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, InvoiceSortColumns.JOB),
      render: (val) => <span className="table-font-mobile">{val}</span>,
    },
    {
      title: "Order",
      key: InvoiceSortColumns.ORDER,
      align: "center",
      width: "192px",
      dataIndex: "order",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, InvoiceSortColumns.ORDER),
      render: (val) => <span className="table-font-mobile">{val}</span>,
    },
    {
      title: "Truck #",
      key: InvoiceSortColumns.TRUCK_NUMBER,
      align: "center",
      width: "192px",
      dataIndex: "truckNumber",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        InvoiceSortColumns.TRUCK_NUMBER,
      ),
      render: (val) => <span className="table-font-mobile">{val}</span>,
    },
    {
      title: "Date",
      key: InvoiceSortColumns.DATE,
      align: "center",
      dataIndex: "date",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, InvoiceSortColumns.DATE),
      render: (val) => <span className="table-font-mobile">{val}</span>,
    },
    {
      title: "Amount",
      key: InvoiceSortColumns.AMOUNT,
      align: "left",
      dataIndex: "amount",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        InvoiceSortColumns.AMOUNT,
      ),
      render: (val) => (
        <span className="table-font-mobile">
          {getPrevDollorFormattedString(val)}
        </span>
      ),
    },
    {
      title: "Invoice",
      key: "invoicePdf",
      align: "center",
      dataIndex: "invoicePdf",
      onCell: () => ({
        onClick: (e) => e.stopPropagation(),
        onMouseDown: (e) => e.stopPropagation(),
        onDoubleClick: (e) => e.stopPropagation(),
      }),
      render: (val, record) => {
        return (
          <img
            src={pdfIcon}
            alt="PDF Icon"
            className={clsx({ [styles.pdfIconStyle]: editAccess })}
            width={30}
            onClick={async () => {
              if (editAccess) await handleFetchingPresignedUrl(record?.id);
            }}
          />
        );
      },
    },
    {
      title: "Status",
      key: "invoiceStatus",
      align: "left",
      dataIndex: "invoiceStatus",
      onCell: () => ({
        onClick: (e) => e.stopPropagation(),
        onMouseDown: (e) => e.stopPropagation(),
        onDoubleClick: (e) => e.stopPropagation(),
      }),
      render: (val, record) => {
        return (
          <Select
            bordered={false}
            options={invoiceStatusOptions}
            dropdownMatchSelectWidth={false}
            suffixIcon={<RiArrowDownSLine width={"20px"} />}
            value={val}
            className={clsx(styles.CustomSelect, {
              [styles.CustomSelectSent]: val === InvoiceStatus.SENT,
              [styles.CustomSelectReviewed]: val === InvoiceStatus.REVIEWED,
              [styles.CustomSelectNotReviewed]:
                val === InvoiceStatus.NOT_REVIEWED,
            })}
            disabled={!editAccess}
            showArrow={editAccess}
            onSelect={async (val) =>
              await handleStatusChange({
                invoiceId: record?.id,
                key: UpdateInvoiceStatusKey.INVOICE_STATUS,
                status: val,
              })
            }
          />
        );
      },
    },
    {
      title: "Quickbook Status",
      key: "syncStatus",
      align: "left",
      dataIndex: "syncStatus",
      onCell: () => ({
        onClick: (e) => e.stopPropagation(),
        onMouseDown: (e) => e.stopPropagation(),
        onDoubleClick: (e) => e.stopPropagation(),
      }),
      render: (val, record) => {
        return (
          <Select
            bordered={false}
            options={quickbookStatusOptions}
            value={val}
            suffixIcon={<RiArrowDownSLine width={"20px"} />}
            className={clsx(styles.CustomSelect, {
              [styles.CustomSelectSynced]: val === QuickbookStatus.SYNCED,
              [styles.CustomSelectNotSynced]:
                val === QuickbookStatus.NOT_SYNCED,
            })}
            dropdownMatchSelectWidth={false}
            disabled={!editAccess}
            showArrow={editAccess}
            onSelect={async (val) =>
              await handleStatusChange({
                invoiceId: record?.id,
                key: UpdateInvoiceStatusKey.SYNC_STATUS,
                status: val,
              })
            }
          />
        );
      },
    },
    {
      title: "",
      key: "actions",
      dataIndex: "actions",
      fixed: "right",
      align: "right",
      render: (_, record) => {
        return (
          <ConditionalRender visible={editAccess}>
            <Popover
              placement={PlacementPositions.LEFT_BOTTOM}
              overlayClassName={styles.CustomerPopover}
              content={
                <>
                  <div
                    className={styles.PopoverContent}
                    onClick={() => handleDownloadFile(record?.invoiceId)}
                  >
                    <img src={DownloadIcon} alt={"Download File"} />
                    Download original file
                  </div>
                  <div
                    className={styles.PopoverContent}
                    onClick={() => handleRecallAction(record?.invoiceId)}
                  >
                    <img src={RecallIcon} alt={"Recall"} />
                    Recall
                  </div>
                  <div
                    className={styles.PopoverContent}
                    onClick={() => openArchiveModal(record?.id)}
                  >
                    <img src={ArchiveIcon} alt={"Archive"} />
                    Archive
                  </div>
                </>
              }
            >
              <EllipsisOutlined
                style={{ fontSize: "30px", color: "#C3C8CD" }}
                className="action-ellipsis-button"
              />
            </Popover>
          </ConditionalRender>
        );
      },
    },
  ];
};

export const getTableFooter = (unsyncedCount?: number, total?: number) => {
  return (
    <div className={styles.footerContainer}>
      <div className={styles.exportableCount}>
        <span>{`Current exportable invoices ${formatNumber(
          Number(unsyncedCount),
        )}/${formatNumber(Number(total))}`}</span>
      </div>
      <div className={styles.tableLegend}>
        <div className={styles.keyCode}>
          <div className={styles.dispatchPendingKey} />
          <span>Rows in red will not be exported</span>
        </div>
        <div className={styles.keyCode}>
          <div className={styles.loadCountMismatchKey} />
          <span>Load Count & Support tickets mismatch</span>
        </div>
        <div className={styles.keyCode}>
          <div className={styles.usedMinimumHoursKey} />
          <span>Minimum Hours applied</span>
        </div>
        <div className={styles.dailySummary}>
          <span>Includes daily summary tickets</span>
        </div>
      </div>
    </div>
  );
};

// Mock data including all responsed - Remove once api is integrated
interface MockApiResponse {
  data: InvoiceDataType[];
  meta: {
    page: number;
    perPage: number;
    totalRecords: number;
    totalPages: number;
  };
}

const jobNames = [
  "New Office Building",
  "Residential Renovation",
  "Warehouse Expansion",
  "Retail Store Remodel",
  "Hospitality Project",
];

const orders = [
  "material - vendor A",
  "equipment - vendor B",
  "services - vendor C",
  "supplies - vendor D",
  "fabrication - vendor E",
];

const getRandomElement = (arr: T[]): T => {
  return arr[Math.floor(Math.random() * arr.length)];
};

const getRandomDate = (): string => {
  const start = new Date(2023, 0, 1).getTime();
  const end = new Date(2025, 11, 31).getTime();
  const randomDate = new Date(start + Math.random() * (end - start));
  return randomDate.toLocaleDateString("en-US");
};

export const generateMockData = (): MockApiResponse => {
  const invoiceStatuses = Object.values(InvoiceStatus);
  const quickbookStatuses = Object.values(QuickbookStatus);
  const invoiceRecordStatuses = [...Object.values(InvoiceRecordStatus), null];

  const data: InvoiceDataType[] = [];
  let invoiceId = 1;

  for (const invStatus of invoiceStatuses) {
    for (const qbStatus of quickbookStatuses) {
      for (const recStatus of invoiceRecordStatuses) {
        data.push({
          invoiceId: invoiceId++,
          companyName: `Customer ${invoiceId}`,
          jobName: getRandomElement(jobNames),
          order: getRandomElement(orders),
          date: getRandomDate(),
          amount: Math.floor(Math.random() * 5000) + 100,
          invoicePdf: "public/invoices/mock.pdf",
          invoiceStatus: invStatus as InvoiceStatus,
          syncStatus: qbStatus as QuickbookStatus,
          status: recStatus,
        });
      }
    }
  }

  const totalRecords = data.length;
  const perPage = 50;
  const totalPages = Math.ceil(totalRecords / perPage);

  return {
    data,
    meta: {
      page: 1,
      perPage: perPage,
      totalRecords: totalRecords,
      totalPages: totalPages,
    },
  };
};

//End of mock api

//Constants

export const getModalConstants = {
  WIDTH: "750px",
  QUICKBOOK: {
    QB_TITLE: "Quickbooks Status Confirmation",
    MARK_SYNCED_LABEL: "Mark as Synced",
    MARK_UNSYNCED_LABEL: "Mark as Unsynced",
    CONFIRMATION_CONTENT: `Are you sure you want to change the invoice status to `,
    SUCCESS_CONTENT: "Quickbook status have been updated successfully",
    FAILURE_CONTENT: "Failed to update the Quickbook status",
  },
  ARCHIVE: {
    ARCHIVE_TITLE: "Delete Invoice",
    ARCHIVE_CONTENT:
      "This invoice will be permanently deleted. Are you sure you want to proceed?",
    ARCHIVE_OK_LABEL: "Confirm",
    ARCHIVE_CANCEL_LABEL: "Cancel",
    ARCHIVE_SUCCESS: "Invoice deleted successfully!",
    ARCHIVE_FAILURE: "Failed to delete invoice!",
  },
  INVOICE_FAILURE: "Failed to open invoice!",
  DOWNLOAD_INVOICE_FAILURE: "Failed to download invoice",
  BULK_STATUS: {
    STATUS_CHANGE_TITLE: "Status change",
    STATUS_UPDATE_SUCCESS: "Invoice status updated successfully!",
    STATUS_UPDATE_FAILURE: "Failed to update invoice status!",
    QB_UPDATE_SUCCESS: "Quickbook status updated successfully!",
    QB_UPDATE_FAILURE: "Failed to update Quickbook status!",
    UPDATE_ALL: "Update all",
  },
};

export const tableSelection = {
  SELECT_ALL: {
    SELECT_ALL_KEY: "select-all-pages",
    SELECT_ALL_LABEL: "Select All Pages",
  },
  DESELECT_ALL: {
    DESELECT_ALL_KEY: "deselect-all",
    DESELECT_ALL_LABEL: "Deselect All",
  },
};
